/* La Caza Cali — Custom PDP behavior
 *
 * Hooks (set in sections/lacaza-product.liquid):
 *   data-pdp-root            wrapper, scope all queries
 *   data-pdp-thumb           gallery thumb buttons
 *   data-pdp-slide           media slides (image/video/model), index-matched to thumbs
 *   data-pdp-variant-media   <script type="application/json"> variant id → media index
 *   data-pdp-fig-current     fig counter span
 *   data-pdp-chip            text variant pill button
 *   data-pdp-variant-id      hidden form input updated on chip click
 *   data-pdp-variants        <script type="application/json"> with full variants array
 *   data-pdp-price           main price display
 *   data-pdp-compare         compare-at price display
 *   data-pdp-cta-price       price suffix inside ATC label
 *   data-pdp-option-value-display   "Finish — Gold" header span
 *   data-pdp-qty-minus / -plus / -input
 *
 * Side effects:
 *   • Pushes URL update on variant change so refresh keeps the variant
 *   • Updates Shopify's `?variant=X` so back button works
 *   • Disables ATC when selected variant is unavailable
 */
(function () {
  'use strict';

  function moneyFormat(cents) {
    // Falls back to en-US dollars without trailing zeros to match the
    // server-rendered Liquid filter (money_without_trailing_zeros).
    var n = Number(cents) / 100;
    var s = n.toFixed(2);
    s = s.replace(/\.00$/, '');
    return '$' + s;
  }

  function bindRoot(root) {
    if (!root || root.dataset.pdpBound === '1') return;
    root.dataset.pdpBound = '1';

    // ----- Variants lookup ---------------------------------------
    var variantsEl = root.querySelector('[data-pdp-variants]');
    var variants = [];
    if (variantsEl) {
      try { variants = JSON.parse(variantsEl.textContent || '[]'); }
      catch (e) { variants = []; }
    }

    var variantMediaEl = root.querySelector('[data-pdp-variant-media]');
    var variantMedia = {};
    if (variantMediaEl) { try { variantMedia = JSON.parse(variantMediaEl.textContent || '{}'); } catch (e) {} }

    var hiddenIdInput = root.querySelector('[data-pdp-variant-id]');
    var priceEl = root.querySelector('[data-pdp-price]');
    var compareEl = root.querySelector('[data-pdp-compare]');
    var ctaPriceEl = root.querySelector('[data-pdp-cta-price]');
    var atcBtn = root.querySelector('button[name="add"]');
    var atcLabel = root.querySelector('[data-pdp-atc-label]');

    function selectedOptionValues() {
      var chips = root.querySelectorAll('[data-pdp-chip].is-active');
      // Build position-indexed array (1-based positions, 0-indexed array)
      var values = [];
      chips.forEach(function (c) {
        var pos = parseInt(c.dataset.optionPosition, 10) - 1;
        values[pos] = c.dataset.optionValue;
      });
      return values;
    }

    function findVariant(values) {
      return variants.find(function (v) {
        return (v.option1 == values[0] || (!values[0] && !v.option1)) &&
               (v.option2 == values[1] || (!values[1] && !v.option2)) &&
               (v.option3 == values[2] || (!values[2] && !v.option3));
      });
    }

    function applyVariant(variant) {
      if (!variant) return;
      if (hiddenIdInput) hiddenIdInput.value = variant.id;

      // Price + compare
      if (priceEl) priceEl.textContent = moneyFormat(variant.price);
      if (compareEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          compareEl.textContent = moneyFormat(variant.compare_at_price);
          compareEl.removeAttribute('hidden');
        } else {
          compareEl.textContent = '';
          compareEl.setAttribute('hidden', 'hidden');
        }
      }

      // ATC label
      if (variant.available) {
        if (atcBtn) atcBtn.removeAttribute('disabled');
        if (atcLabel) {
          atcLabel.innerHTML = 'Add to cart — <span data-pdp-cta-price>' + moneyFormat(variant.price) + '</span>';
        }
      } else {
        if (atcBtn) atcBtn.setAttribute('disabled', 'disabled');
        if (atcLabel) atcLabel.textContent = 'Sold out';
      }

      // Gallery — jump to the variant's featured media slide when mapped.
      var slideIdx = variantMedia[String(variant.id)];
      if (slideIdx !== null && slideIdx !== undefined) showSlide(slideIdx);

      // URL update so refresh / back-button preserve variant
      if (variant.id && window.history && window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    // ----- Variant chip clicks -----------------------------------
    root.querySelectorAll('[data-pdp-chip]').forEach(function (chip) {
      chip.addEventListener('click', function () {
        var pos = chip.dataset.optionPosition;
        var val = chip.dataset.optionValue;

        // Mark this chip active in its option group
        var groupChips = root.querySelectorAll('[data-pdp-chip][data-option-position="' + pos + '"]');
        groupChips.forEach(function (c) {
          var on = (c === chip);
          c.classList.toggle('is-active', on);
          c.setAttribute('aria-checked', on ? 'true' : 'false');
        });

        // Update the "Finish — Gold" header span for this option
        var wrap = chip.closest('[data-pdp-option-wrap]');
        if (wrap) {
          var disp = wrap.querySelector('[data-pdp-option-value-display]');
          if (disp) disp.textContent = val;
        }

        var variant = findVariant(selectedOptionValues());
        applyVariant(variant);
      });
    });

    // ----- Quantity stepper --------------------------------------
    var qtyInput = root.querySelector('[data-pdp-qty-input]');
    var qtyMinus = root.querySelector('[data-pdp-qty-minus]');
    var qtyPlus = root.querySelector('[data-pdp-qty-plus]');
    function clampQty(n) { return Math.max(1, isNaN(n) ? 1 : n); }
    if (qtyMinus && qtyInput) {
      qtyMinus.addEventListener('click', function () {
        qtyInput.value = clampQty(parseInt(qtyInput.value, 10) - 1);
      });
    }
    if (qtyPlus && qtyInput) {
      qtyPlus.addEventListener('click', function () {
        qtyInput.value = clampQty(parseInt(qtyInput.value, 10) + 1);
      });
    }
    if (qtyInput) {
      qtyInput.addEventListener('change', function () {
        qtyInput.value = clampQty(parseInt(qtyInput.value, 10));
      });
    }

    // ----- Gallery slides ----------------------------------------
    var slides = root.querySelectorAll('[data-pdp-slide]');
    var thumbs = root.querySelectorAll('[data-pdp-thumb]');
    var figCurrent = root.querySelector('[data-pdp-fig-current]');

    function showSlide(idx) {
      slides.forEach(function (s) {
        var on = parseInt(s.dataset.pdpSlide, 10) === idx;
        s.classList.toggle('is-active', on);
        var v = s.querySelector('video');
        if (v) {
          if (on) { if (v.play) v.play().catch(function () {}); }
          else { v.pause(); }
        }
      });
      thumbs.forEach(function (t) {
        var on = parseInt(t.dataset.pdpThumb, 10) === idx;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      if (figCurrent) figCurrent.textContent = String(idx + 1).padStart(2, '0');
    }

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        showSlide(parseInt(thumb.dataset.pdpThumb, 10));
      });
    });

    // ----- Accordion +/− icon swap on toggle ---------------------
    // Native <details> handles open/close; we just need to mirror
    // the open state for the +/− CSS swap (CSS handles the glyph
    // via [open] selector, but we also flip aria-expanded for AT).
    root.querySelectorAll('.lacaza-pdp__acc-item').forEach(function (item) {
      var summary = item.querySelector('summary');
      if (!summary) return;
      summary.setAttribute('aria-expanded', item.hasAttribute('open') ? 'true' : 'false');
      item.addEventListener('toggle', function () {
        summary.setAttribute('aria-expanded', item.hasAttribute('open') ? 'true' : 'false');
      });
    });
  }

  function init() {
    document.querySelectorAll('[data-pdp-root]').forEach(bindRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
